import React from 'react';
import {
  CheckCircle as CheckCircleIcon,
  AltRoute as AltRouteIcon,
  KeyboardArrowUp as ArrowUpwardIcon,
  Undo as UndoIcon,
  Assignment as AssignmentIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';

export interface EligibilityData {
  ifEligible: boolean | null;
  gradeIssues?: string[];
  missingCourses?: string[];
  calculatedAverage?: number;
  programAverage?: number;
  alertMessage?: string;
  nextYearSuggestion?: string;
  coursesUsedInAverage?: string[];
}

interface EligibilityIconProps {
  eligibility: EligibilityData | null;
  userGrade: string;
  size?: number;
  className?: string;
}

const EligibilityIcon: React.FC<EligibilityIconProps> = ({
  eligibility,
  userGrade,
  size = 20,
  className = '',
}) => {
  const isGrade10 = userGrade === '10';

  // Tooltip content
  const getTooltipTitle = (eligibility: EligibilityData | null) => {
    if (!eligibility) {
      return 'Eligibility not checked';
    }
    let title = eligibility.alertMessage || 'Eligibility checked';
    if (eligibility.nextYearSuggestion && eligibility.nextYearSuggestion !== "") {
      title += `\n💡 ${eligibility.nextYearSuggestion}`;
    }
    return title;
  };

  const tooltip = getTooltipTitle(eligibility);

  if (!eligibility) {
    return (
      <span
        className={`eligibility-indicator unknown ${className}`}
        style={{
          display: 'inline-flex',
          width: size,
          height: size,
          borderRadius: '50%',
          backgroundColor: '#9e9e9e',
          color: 'white',
          alignItems: 'center',
          justifyContent: 'center',
          verticalAlign: 'middle',
          border: `2px solid #9e9e9e`,
          boxSizing: 'border-box',
        }}
        title={tooltip}
      >
        <HelpIcon style={{ fontSize: size * 0.75 }} />
      </span>
    );
  }

  if (eligibility.ifEligible === true) {
    if (isGrade10) {
      // Grade 10: Fosterway green circle with white up arrow (ArrowUpwardIcon)
      return (
        <span
          className={`eligibility-indicator grade10-eligible ${className}`}
          style={{
            display: 'inline-flex',
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: '#8dd439',
            color: 'white',
            alignItems: 'center',
            justifyContent: 'center',
            verticalAlign: 'middle',
            border: `2px solid #8dd439`,
            boxSizing: 'border-box',
          }}
          title={tooltip}
        >
          <ArrowUpwardIcon style={{ fontSize: size * 0.7 }} />
        </span>
      );
    } else {
      // Other grades: Use CheckCircleIcon directly like profile pages
      return (
        <Tooltip title={tooltip}>
          <CheckCircleIcon 
            className={`eligibility-indicator eligible ${className}`}
            sx={{ 
              color: '#4caf50', // Material-UI success.main color
              fontSize: size,
              verticalAlign: 'middle'
            }}
          />
        </Tooltip>
      );
    }
  } else if (eligibility.ifEligible === false) {
    if (isGrade10) {
      const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
      const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;
      const hasEarlyPrereqs = eligibility.alertMessage?.includes("Next year's courses will be prerequisites");

      if (hasGradeIssues || hasEarlyPrereqs) {
        // Grade 10: Yellow circle with white up arrow (ArrowUpwardIcon)
        return (
          <span
            className={`eligibility-indicator grade10-issue ${className}`}
            style={{
              display: 'inline-flex',
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: '#ffc107',
              color: 'white',
              alignItems: 'center',
              justifyContent: 'center',
              verticalAlign: 'middle',
              border: `2px solid #ffc107`,
              boxSizing: 'border-box',
            }}
            title={tooltip}
          >
            <ArrowUpwardIcon style={{ fontSize: size * 0.7 }} />
          </span>
        );
      } else if (hasMissingCourses) {
        // Grade 10: Blue circle with white undo arrow (UndoIcon)
        return (
          <span
            className={`eligibility-indicator grade10-missing ${className}`}
            style={{
              display: 'inline-flex',
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: '#1976d2',
              color: 'white',
              alignItems: 'center',
              justifyContent: 'center',
              verticalAlign: 'middle',
              border: `2px solid #1976d2`,
              boxSizing: 'border-box',
            }}
            title={tooltip}
          >
            <UndoIcon style={{ fontSize: size * 0.7 }} />
          </span>
        );
      } else {
        // Grade 10: Fallback blue circle with undo arrow (UndoIcon)
        return (
          <span
            className={`eligibility-indicator grade10-fallback ${className}`}
            style={{
              display: 'inline-flex',
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: '#1976d2',
              color: 'white',
              alignItems: 'center',
              justifyContent: 'center',
              verticalAlign: 'middle',
              border: `2px solid #1976d2`,
              boxSizing: 'border-box',
            }}
            title={tooltip}
          >
            <UndoIcon style={{ fontSize: size * 0.7 }} />
          </span>
        );
      }
    } else {
      // Other grades: Check if this is a Grade 12 student with low average
      if (userGrade === '12' &&
          eligibility.calculatedAverage &&
          eligibility.programAverage &&
          eligibility.calculatedAverage < eligibility.programAverage &&
          (!eligibility.missingCourses || eligibility.missingCourses.length === 0) &&
          (!eligibility.gradeIssues || eligibility.gradeIssues.length === 0)) {
        // Grade 12: Blue circle with alt route icon (AltRouteIcon)
        return (
          <span
            className={`eligibility-indicator grade12-low-avg ${className}`}
            style={{
              display: 'inline-flex',
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: '#1976d2',
              color: 'white',
              alignItems: 'center',
              justifyContent: 'center',
              verticalAlign: 'middle',
              border: `2px solid #1976d2`,
              boxSizing: 'border-box',
            }}
            title={tooltip}
          >
            <AltRouteIcon style={{ fontSize: size * 0.7 }} />
          </span>
        );
      } else {
        // Other grades: Blue circle with alt route icon (AltRouteIcon)
        return (
          <span
            className={`eligibility-indicator not-eligible ${className}`}
            style={{
              display: 'inline-flex',
              width: size,
              height: size,
              borderRadius: '50%',
              backgroundColor: '#1976d2',
              color: 'white',
              alignItems: 'center',
              justifyContent: 'center',
              verticalAlign: 'middle',
              border: `2px solid #1976d2`,
              boxSizing: 'border-box',
            }}
            title={tooltip}
          >
            <AltRouteIcon style={{ fontSize: size * 0.7 }} />
          </span>
        );
      }
    }
  } else { // ifEligible is null
    const hasGradeIssues = eligibility.gradeIssues && eligibility.gradeIssues.length > 0;
    const hasMissingCourses = eligibility.missingCourses && eligibility.missingCourses.length > 0;

    // Check if this is a Grade 12 student with low average (even when ifEligible is null)
    if (userGrade === '12' &&
        eligibility.calculatedAverage &&
        eligibility.programAverage &&
        eligibility.calculatedAverage < eligibility.programAverage &&
        (!hasMissingCourses) &&
        (!hasGradeIssues)) {
      // Grade 12: Blue circle with alt route icon (AltRouteIcon)
      return (
        <span
          className={`eligibility-indicator grade12-low-avg-null ${className}`}
          style={{
            display: 'inline-flex',
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: '#1976d2',
            color: 'white',
            alignItems: 'center',
            justifyContent: 'center',
            verticalAlign: 'middle',
            border: `2px solid #1976d2`,
            boxSizing: 'border-box',
          }}
          title={tooltip}
        >
          <AltRouteIcon style={{ fontSize: size * 0.7 }} />
        </span>
      );
    } else if (hasGradeIssues || hasMissingCourses) {
      // Show blue alt route icon for missing courses or grade issues (AltRouteIcon)
      return (
        <span
          className={`eligibility-indicator has-issues ${className}`}
          style={{
            display: 'inline-flex',
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: '#1976d2',
            color: 'white',
            alignItems: 'center',
            justifyContent: 'center',
            verticalAlign: 'middle',
            border: `2px solid #1976d2`,
            boxSizing: 'border-box',
          }}
          title={tooltip}
        >
          <AltRouteIcon style={{ fontSize: size * 0.7 }} />
        </span>
      );
    } else {
      // No issues found - show green checklist (AssignmentIcon)
      return (
        <span
          className={`eligibility-indicator no-prereqs ${className}`}
          style={{
            display: 'inline-flex',
            width: size,
            height: size,
            borderRadius: '50%',
            backgroundColor: '#8dd439',
            color: 'white',
            alignItems: 'center',
            justifyContent: 'center',
            verticalAlign: 'middle',
            border: `2px solid #8dd439`,
            boxSizing: 'border-box',
          }}
          title={tooltip}
        >
          <AssignmentIcon style={{ fontSize: size * 0.7 }} />
        </span>
      );
    }
  }
};

export default EligibilityIcon;
